import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import type { AssertionDisplayMode } from "../../assertions";
import { useCellContentDialogContext } from "../cell-content-dialog-context";

import { CopyButton } from "@/components/ui/copy-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SaveStatus from "@/components/ui/SaveStatus";
import { BasicTooltip, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import {
  CellContentType,
  getCopyTextForClassifiedContent,
} from "@/routes/smart-table/utils/contentType";

const MEDIA_TYPES = new Set([
  CellContentType.IMAGE,
  CellContentType.AUDIO,
  CellContentType.VIDEO,
  CellContentType.PDF,
  CellContentType.FILE,
  CellContentType.MEDIA_COLLECTION,
  CellContentType.MULTI_CONTENT,
]);

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  [CellContentType.PLAIN_TEXT]: "Text",
  [CellContentType.MARKDOWN]: "Markdown",
  [CellContentType.JSON]: "JSON",
  [CellContentType.LINK]: "Link",
  [CellContentType.IMAGE]: "Media",
  [CellContentType.AUDIO]: "Media",
  [CellContentType.VIDEO]: "Media",
  [CellContentType.PDF]: "Media",
  [CellContentType.FILE]: "Media",
  [CellContentType.PRICE]: "Price",
  [CellContentType.DURATION]: "Duration",
  [CellContentType.MEDIA_COLLECTION]: "Media",
  [CellContentType.MULTI_CONTENT]: "Media",
  compare_text: "Text",
  trace: "Trace",
  request_log: "Request Log",
};

const DEFAULT_SWITCHABLE_TYPES = [
  CellContentType.PLAIN_TEXT,
  CellContentType.MARKDOWN,
  CellContentType.JSON,
];

const CellContentHeader = observer(() => {
  const {
    column,
    rowIndex,
    displayedRowIndex,
    totalRows,
    content,
    sourceContentType,
    displayType,
    setDisplayType,
    displayTypeOptions,
    navigateUp,
    navigateDown,
    close,
    cellUpdatedAt,
    isSavingCell,
    isContentLoading,
    hideRowNavigation,
    hideSavedAt,
    headerContextLabel,
    snippetVersionsLabel,
  } = useCellContentDialogContext();

  const visibleRowIndex = displayedRowIndex ?? rowIndex;
  const atFirst = displayedRowIndex === null || displayedRowIndex <= 0;
  const atLast = displayedRowIndex === null || displayedRowIndex >= totalRows - 1;
  const isMedia = MEDIA_TYPES.has(sourceContentType);
  const isLink = sourceContentType === CellContentType.LINK;
  const isMetric =
    sourceContentType === CellContentType.PRICE || sourceContentType === CellContentType.DURATION;
  const resolvedTypeOptions =
    displayTypeOptions ??
    DEFAULT_SWITCHABLE_TYPES.map(type => ({
      value: type,
      label: DISPLAY_TYPE_LABELS[type] ?? type,
    }));
  let mediaTypeOptions = resolvedTypeOptions;
  if (isMedia) {
    mediaTypeOptions = [
      { value: sourceContentType, label: "Media" },
      { value: CellContentType.JSON, label: "JSON" },
      { value: CellContentType.PLAIN_TEXT, label: "Text" },
    ];
  } else if (isLink) {
    mediaTypeOptions = [
      { value: CellContentType.LINK, label: "Link" },
      { value: CellContentType.PLAIN_TEXT, label: "Text" },
    ];
  } else if (isMetric) {
    mediaTypeOptions = [
      {
        value: sourceContentType,
        label: DISPLAY_TYPE_LABELS[sourceContentType] ?? "Metric",
      },
      { value: CellContentType.PLAIN_TEXT, label: "Text" },
    ];
  }
  const isSwitchable = mediaTypeOptions.some(option => option.value === displayType);

  const getCopyText = useCallback(() => getCopyTextForClassifiedContent(content), [content]);

  const navBtnClass =
    "flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-25";
  const actionBtnClass =
    "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";

  return (
    <div className="flex items-center gap-3 border-b border-border/60 bg-background px-4 py-2.5">
      <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={100}>
        {!hideRowNavigation && (
          <div className="flex flex-col gap-px">
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={atFirst}
                  onClick={navigateUp}
                  className={navBtnClass}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipPrimitive.Portal>
                <TooltipContent side="right">Previous row (↑)</TooltipContent>
              </TooltipPrimitive.Portal>
            </Tooltip>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={atLast}
                  onClick={navigateDown}
                  className={navBtnClass}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipPrimitive.Portal>
                <TooltipContent side="right">Next row (↓)</TooltipContent>
              </TooltipPrimitive.Portal>
            </Tooltip>
          </div>
        )}
      </TooltipPrimitive.Provider>

      <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="truncate text-sm font-medium text-foreground">
          {column?.title ?? "Cell"}
        </span>
        <span className="flex-shrink-0 text-xs text-muted-foreground/60">·</span>
        <span className="flex-shrink-0 text-xs text-muted-foreground">
          {headerContextLabel ??
            `Row ${visibleRowIndex + 1}${totalRows > 0 ? ` of ${totalRows}` : ""}`}
        </span>
        {!hideSavedAt && (
          <>
            <span className="flex-shrink-0 text-xs text-muted-foreground/60">·</span>
            <SaveStatus
              isSaving={isSavingCell}
              savedAt={cellUpdatedAt}
              variant="dialog"
              className="flex-shrink-0 gap-1 self-center"
            />
          </>
        )}
        {snippetVersionsLabel && (
          <>
            <span className="flex-shrink-0 text-xs text-muted-foreground/60">·</span>
            <BasicTooltip content="Snippet versions used for this output" side="bottom">
              <span className="truncate text-xs text-muted-foreground">{snippetVersionsLabel}</span>
            </BasicTooltip>
          </>
        )}
      </div>

      <div className="flex items-center gap-0.5">
        {!isContentLoading && isSwitchable && (
          <>
            <Select
              value={displayType}
              onValueChange={(value: string) =>
                setDisplayType(value as CellContentType | AssertionDisplayMode | "compare_text")
              }
            >
              <SelectTrigger className="h-6 w-auto gap-1 border-0 bg-transparent px-1.5 py-0 text-xs text-muted-foreground shadow-none ring-0 ring-offset-0 hover:bg-muted hover:text-foreground focus:ring-0 focus:ring-offset-0">
                <SelectValue>
                  {mediaTypeOptions.find(option => option.value === displayType)?.label ??
                    DISPLAY_TYPE_LABELS[displayType] ??
                    displayType}
                </SelectValue>
              </SelectTrigger>
              <SelectContent align="end">
                {mediaTypeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mx-0.5 h-4 w-px bg-border/50" />
          </>
        )}

        {!isContentLoading && content.type !== CellContentType.EMPTY && (
          <BasicTooltip content="Copy" side="bottom" delayDuration={400}>
            <CopyButton
              getText={getCopyText}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
            />
          </BasicTooltip>
        )}

        <div className="mx-0.5 h-4 w-px bg-border/50" />

        <BasicTooltip content="Close (Esc)" side="bottom" delayDuration={400}>
          <button type="button" onClick={close} className={actionBtnClass}>
            <X className="h-3.5 w-3.5" />
          </button>
        </BasicTooltip>
      </div>
    </div>
  );
});

CellContentHeader.displayName = "CellContentHeader";

export default CellContentHeader;
